import React, { useState, useEffect } from "react";
import { InputBox } from "@/components/ui/InputBox";
import SelectBox from "@/components/ui/SelectBox";
import { Button } from "@/components/ui/Button";
import TextAreaBox from "@/components/ui/TextAreaBox";
import ModalLayout from "@/components/layout/ModalLayout";
import { toast } from "sonner";
import ModalHeader from "@/components/ui/ModalHeader";
import { useDriverCodeInsert } from "@/hooks/standard/st_driver_code/useDriverCode";


interface AddModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: DriverCodeFormData) => void;
}

export interface DriverCodeFormData {
    stDriverName: string;
    stDriverMobileTel: string;
    stDriverLicenseStartDate: string;
    stDriverLicenseEndDate: string;
    stDriverHazmatEduStartDate: string;   // 유해물질 교육일
    stDriverHazmatEduEndDate: string;     // 유해물질 교육종료일
    stDriverDangerEduStartDate: string;   // 위험물 교육일
    stDriverDangerEduEndDate: string;     // 위험물 교육종료일
    stDriverRemark: string;
    stDriverEmploymentStatus: string;     // 재직상태 (Y: 재직, N: 퇴사)
}

export default function AddModal({ isOpen, onClose, onSave }: AddModalProps) {
    const insertMutation = useDriverCodeInsert();
    const [formData, setFormData] = useState<DriverCodeFormData>({
        stDriverName: "",
        stDriverMobileTel: "",
        stDriverLicenseStartDate: "",
        stDriverLicenseEndDate: "",
        stDriverHazmatEduStartDate: "",
        stDriverHazmatEduEndDate: "",
        stDriverDangerEduStartDate: "",
        stDriverDangerEduEndDate: "",
        stDriverRemark: "",
        stDriverEmploymentStatus: "Y", // 기본값: 재직
    });

    const employmentStatusOptions = [
        { value: "Y", label: "재직" },
        { value: "N", label: "퇴사" },
    ];

    // 날짜 형식 변환 (YYYYMMDD 또는 YYYY-MM-DD 모두 지원)
    const formatDate = (dateStr: string): string | null => {
        if (!dateStr || dateStr.trim() === "") return null;
        // 이미 YYYY-MM-DD 형식이면 그대로 반환
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            return dateStr;
        }
        // YYYYMMDD 형식이면 YYYY-MM-DD로 변환
        if (/^\d{8}$/.test(dateStr)) {
            return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
        }
        return null;
    };

    const handleInputChange = (field: keyof DriverCodeFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        // 필수 필드 검증
        if (!formData.stDriverName.trim()) {
            toast.warning("기사 이름을 입력해주세요.");
            return;
        }

        // 전화번호 유효성 검사 (000-0000-0000 형식)
        if (formData.stDriverMobileTel.trim()) {
            const phoneRegex = /^\d{3}-\d{4}-\d{4}$/;
            if (!phoneRegex.test(formData.stDriverMobileTel)) {
                toast.warning("기사 전화번호는 010-0000-0000 형식으로 입력해주세요.");
                return;
            }
        }
        
        try {
            await insertMutation.mutateAsync({
                stDriverName: formData.stDriverName,
                stDriverMobileTel: formData.stDriverMobileTel,
                stDriverLicenseStartDate: formatDate(formData.stDriverLicenseStartDate) || undefined,
                stDriverLicenseEndDate: formatDate(formData.stDriverLicenseEndDate) || undefined,
                stDriverHazmatEduStartDate: formatDate(formData.stDriverHazmatEduStartDate) || undefined,
                stDriverHazmatEduEndDate: formatDate(formData.stDriverHazmatEduEndDate) || undefined,
                stDriverDangerEduStartDate: formatDate(formData.stDriverDangerEduStartDate) || undefined,
                stDriverDangerEduEndDate: formatDate(formData.stDriverDangerEduEndDate) || undefined,
                stDriverRemark: formData.stDriverRemark,
                stDriverEmploymentStatus: formData.stDriverEmploymentStatus,
            } as any);
            toast.success("기사 코드가 추가되었습니다.");
            handleCancel();
            onSave(formData);
        } catch (error) {
            console.error("저장 실패:", error);
        }
    };

    const handleCancel = () => {
        // 폼 데이터 초기화
        setFormData({
            stDriverName: "",
            stDriverMobileTel: "",
            stDriverLicenseStartDate: "",
            stDriverLicenseEndDate: "",
            stDriverHazmatEduStartDate: "",
            stDriverHazmatEduEndDate: "",
            stDriverDangerEduStartDate: "",
            stDriverDangerEduEndDate: "",
            stDriverRemark: "",
            stDriverEmploymentStatus: "Y",
        });
        onClose();
    };

    return (
        <ModalLayout isOpen={isOpen} onClose={handleCancel} title="기사 코드 추가" maxWidth="max-w-2xl">
            {/* 기본 정보 섹션 */}
            <div className="rounded-lg">
                <ModalHeader title="기본 정보" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <InputBox
                            required={true}
                            label="기사 이름"
                            value={formData.stDriverName}
                            onChange={e => handleInputChange("stDriverName", e.target.value)}
                            placeholder="기사 이름 입력"
                            className="w-full"
                        />
                    </div>
                    <div>
                        <InputBox
                            required={true}
                            label="전화번호"
                            value={formData.stDriverMobileTel}
                            onChange={e => handleInputChange("stDriverMobileTel", e.target.value)}
                            placeholder="010-0000-0000"
                            className="w-full"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <SelectBox
                            label="재직 상태"
                            value={formData.stDriverEmploymentStatus}
                            onChange={value => handleInputChange("stDriverEmploymentStatus", value)}
                            options={employmentStatusOptions}
                            className="w-full"
                        />
                    </div>
                </div>

                <ModalHeader title="추가 정보" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <InputBox
                            label="면허 시작일"
                            value={formData.stDriverLicenseStartDate}
                            onChange={e => handleInputChange("stDriverLicenseStartDate", e.target.value)}
                            placeholder="YYYY-MM-DD"
                            className="w-full"
                        />
                    </div>
                    <div>
                        <InputBox
                            label="면허 종료일"
                            value={formData.stDriverLicenseEndDate}
                            onChange={e => handleInputChange("stDriverLicenseEndDate", e.target.value)}
                            placeholder="YYYY-MM-DD"
                            className="w-full"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <InputBox
                            label="유해물질 교육일"
                            value={formData.stDriverHazmatEduStartDate}
                            onChange={e => handleInputChange("stDriverHazmatEduStartDate", e.target.value)}
                            placeholder="YYYY-MM-DD"
                            className="w-full"
                        />
                    </div>
                    <div>
                        <InputBox
                            label="유해물질 교육종료일"
                            value={formData.stDriverHazmatEduEndDate}
                            onChange={e => handleInputChange("stDriverHazmatEduEndDate", e.target.value)}
                            placeholder="YYYY-MM-DD"
                            className="w-full"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <InputBox
                            label="위험물 교육일"
                            value={formData.stDriverDangerEduStartDate}
                            onChange={e => handleInputChange("stDriverDangerEduStartDate", e.target.value)}
                            placeholder="YYYY-MM-DD"
                            className="w-full"
                        />
                    </div>
                    <div>
                        <InputBox
                            label="위험물 교육종료일"
                            value={formData.stDriverDangerEduEndDate}
                            onChange={e => handleInputChange("stDriverDangerEduEndDate", e.target.value)}
                            placeholder="YYYY-MM-DD"
                            className="w-full"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-1 gap-1 mb-4">
                    <div>
                        <TextAreaBox
                            label="비고"
                            value={formData.stDriverRemark}
                            onChange={e => handleInputChange("stDriverRemark", e.target.value)}
                            placeholder="비고 입력"
                            className="w-full"
                            rows={1}
                        />
                    </div>
                </div>
                {/* 모달 푸터 */}
                <div className="flex items-center gap-3 mt-6">
                    <div className="flex justify-start w-full">
                        <Button variant="white" onClick={handleCancel} className="py-[10px]">
                            취소
                        </Button>
                    </div>
                    <div className="flex justify-end w-full">
                        <Button variant="primary" onClick={handleSave} className="py-[10px]">
                            저장
                        </Button>
                    </div>
                </div>
            </div>
        </ModalLayout>
    );
}